// frontend/src/pages/admin/AnalyticsPage.js
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  FaChartLine, FaChartBar, FaChartPie, FaCalendarAlt, 
  FaMoneyBillWave, FaBoxes, FaUsers, 
  FaFilter, FaDownload, FaSyncAlt, FaWarehouse, FaExclamationTriangle
} from 'react-icons/fa';
import { FaArrowTrendUp } from 'react-icons/fa6';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  AreaChart, Area
} from 'recharts';
import {
  fetchProfitAnalysis,
  fetchSalesTrends,
  fetchSeasonalAnalysis,
  fetchProductPerformance,
  fetchInventoryHealth,
  fetchAnalyticsDashboard
} from '../../actions/analyticsActions';
import { fetchWarehouses } from '../../actions/inventoryActions';
import { listProducts } from '../../actions/productActions';
import FilterProductSelector from '../../components/inventory/FilterProductSelector';
import Loader from '../../components/ui/Loader';
import Message from '../../components/ui/Message';

const AnalyticsPage = () => {
  const dispatch = useDispatch();
  
  // Redux state
  const { data: profitData, loading: profitLoading, error: profitError } = useSelector(state => state.profitAnalysis);
  const { data: trendsData, loading: trendsLoading, error: trendsError } = useSelector(state => state.salesTrends);
  const { monthly: monthlyData, seasonal: seasonalData, loading: seasonalLoading, error: seasonalError } = useSelector(state => state.seasonalAnalysis);
  const { 
    topSelling, 
    mostProfitable, 
    turnoverAnalysis, 
    loading: performanceLoading,
    error: performanceError 
  } = useSelector(state => state.productPerformance);
  const { data: healthData, loading: healthLoading, error: healthError } = useSelector(state => state.inventoryHealth);
  const { 
    overall, 
    inventory, 
    today, 
    loading: dashboardLoading,
    error: dashboardError 
  } = useSelector(state => state.analyticsDashboard);
  const { warehouses } = useSelector(state => state.warehouseList);
  
  // Local state for filters
  const [filters, setFilters] = useState({
    start_date: '',
    end_date: '',
    product_id: '',
    warehouse_id: '',
    period: 'month'
  });
  
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // 预设日期范围
  const dateRanges = [
    { label: '今天', days: 0 },
    { label: '最近7天', days: 7 },
    { label: '最近30天', days: 30 },
    { label: '最近90天', days: 90 },
    { label: '最近一年', days: 365 }
  ];

  const ErrorDisplay = ({ error, title }) => (
    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
      <div className="flex items-center">
        <FaExclamationTriangle className="text-red-500 mr-2" />
        <h4 className="text-red-800 font-medium">{title}</h4>
      </div>
      <p className="text-red-700 mt-1">{error}</p>
    </div>
  );

  const formatNumber = (num) => {
    return new Intl.NumberFormat('zh-CN').format(num || 0);
  };

  const NoDataDisplay = ({ message = "暂无数据" }) => (
    <div className="text-center text-gray-500 py-12">
      <FaChartLine className="text-4xl text-gray-300 mx-auto mb-4" />
      <p className="text-lg">{message}</p>
    </div>
  );

  useEffect(() => {
    dispatch(fetchWarehouses());
    dispatch(listProducts());
    loadAnalyticsData();
  }, [dispatch]);
  
  useEffect(() => {
    loadAnalyticsData();
  }, [filters, activeTab]);
  
  const loadAnalyticsData = () => {
    switch (activeTab) {
      case 'dashboard':
        dispatch(fetchAnalyticsDashboard(filters));
        dispatch(fetchSalesTrends(filters));
        dispatch(fetchSeasonalAnalysis(filters));
        break;
      case 'profit':
        dispatch(fetchProfitAnalysis(filters));
        break;
      case 'trends':
        dispatch(fetchSalesTrends(filters));
        break;
      case 'seasonal':
        dispatch(fetchSeasonalAnalysis(filters));
        break;
      case 'performance':
        dispatch(fetchProductPerformance(filters));
        break;
      case 'health':
        dispatch(fetchInventoryHealth(filters));
        break;
      default:
        dispatch(fetchAnalyticsDashboard(filters));
    }
  };
  
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };
  
  const setDateRange = (days) => {
    const endDate = new Date();
    const startDate = new Date();
    
    if (days === 0) {
      // 今天
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);
    } else {
      startDate.setDate(startDate.getDate() - days);
    }
    
    setFilters(prev => ({
      ...prev,
      start_date: startDate.toISOString().split('T')[0],
      end_date: endDate.toISOString().split('T')[0]
    }));
  };
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('zh-CN', {
      style: 'currency',
      currency: 'CNY'
    }).format(amount || 0);
  };
  
  // 修复 Tooltip formatter
  const formatTooltipValue = (value, name, props) => {
    let displayName = name;
    let displayValue = value;
    
    switch (name) {
      case 'total_revenue':
        displayName = '收入';
        displayValue = formatCurrency(value);
        break;
      case 'total_units':
        displayName = '销量';
        displayValue = `${value}瓶`;
        break;
      case 'order_count':
        displayName = '订单数';
        displayValue = `${value}单`;
        break;
      case 'avg_unit_price':
        displayName = '平均单价';
        displayValue = formatCurrency(value);
        break;
      default:
        displayValue = value;
    }
    
    return [displayValue, displayName];
  };
  
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];
  
  // Dashboard Cards Component
  const DashboardCards = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg shadow-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-blue-100 text-sm">总收入</p>
            <p className="text-2xl font-bold">{formatCurrency(overall?.total_revenue)}</p>
            <p className="text-blue-100 text-xs">今日: {formatCurrency(today?.today_revenue)}</p>
          </div>
          <FaMoneyBillWave className="text-3xl text-blue-200" />
        </div>
      </div>
      
      <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg shadow-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-green-100 text-sm">总订单数</p>
            <p className="text-2xl font-bold">{overall?.total_orders || 0}</p>
            <p className="text-green-100 text-xs">今日: {today?.today_orders || 0}</p>
          </div>
          <FaChartLine className="text-3xl text-green-200" />
        </div>
      </div>
      
      <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg shadow-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-purple-100 text-sm">库存总价值</p>
            <p className="text-2xl font-bold">{formatCurrency(inventory?.total_inventory_value)}</p>
            <p className="text-purple-100 text-xs">{inventory?.total_inventory_units || 0} 瓶</p>
          </div>
          <FaBoxes className="text-3xl text-purple-200" />
        </div>
      </div>
      
      <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg shadow-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-orange-100 text-sm">客户数量</p>
            <p className="text-2xl font-bold">{overall?.unique_customers || 0}</p>
            <p className="text-orange-100 text-xs">平均订单: {formatCurrency(overall?.avg_order_value)}</p>
          </div>
          <FaUsers className="text-3xl text-orange-200" />
        </div>
      </div>
    </div>
  );
  
  // Filter Bar Component
  const FilterBar = () => (
    <div className="bg-white rounded-lg shadow-md p-6 mb-6">
      <div className="flex flex-wrap gap-4 items-end">
        <div className="flex-1 min-w-0">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            产品筛选
          </label>
          <FilterProductSelector
            value={filters.product_id}
            onChange={(value) => handleFilterChange('product_id', value)}
            placeholder="全部产品"
            maxDisplayItems={20}
          />
        </div>
        
        <div className="flex-1 min-w-0">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            仓库筛选
          </label>
          <select
            value={filters.warehouse_id}
            onChange={(e) => handleFilterChange('warehouse_id', e.target.value)}
            className="w-full p-2 border rounded-lg"
          >
            <option value="">全部仓库</option>
            {warehouses?.map(warehouse => (
              <option key={warehouse.id} value={warehouse.id}>
                {warehouse.name}
              </option>
            ))}
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            开始日期
          </label>
          <input
            type="date"
            value={filters.start_date}
            onChange={(e) => handleFilterChange('start_date', e.target.value)}
            className="p-2 border rounded-lg"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            结束日期
          </label>
          <input
            type="date"
            value={filters.end_date}
            onChange={(e) => handleFilterChange('end_date', e.target.value)}
            className="p-2 border rounded-lg"
          />
        </div>
        
        {activeTab === 'trends' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              统计周期
            </label>
            <select
              value={filters.period}
              onChange={(e) => handleFilterChange('period', e.target.value)}
              className="p-2 border rounded-lg"
            >
              <option value="day">按天</option>
              <option value="week">按周</option>
              <option value="month">按月</option>
              <option value="quarter">按季度</option>
              <option value="year">按年</option>
            </select>
          </div>
        )}
        
        <button
          onClick={loadAnalyticsData}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
        >
          <FaSyncAlt className="mr-2" />
          刷新
        </button>
      </div>
      
      {/* 快速日期选择 */}
      <div className="mt-4 flex flex-wrap gap-2">
        <span className="text-sm text-gray-600 self-center mr-2">快速选择:</span>
        {dateRanges.map((range) => (
          <button
            key={range.label}
            onClick={() => setDateRange(range.days)}
            className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-full"
          >
            {range.label}
          </button>
        ))}
      </div>
    </div>
  );
  
  // Tab Navigation
  const TabNavigation = () => (
    <div className="bg-white rounded-lg shadow-md mb-6">
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8 px-6">
          {[
            { key: 'dashboard', label: '总览', icon: FaChartLine },
            { key: 'profit', label: '利润分析', icon: FaMoneyBillWave },
            { key: 'trends', label: '销售趋势', icon: FaArrowTrendUp },
            { key: 'seasonal', label: '季节分析', icon: FaCalendarAlt },
            { key: 'performance', label: '产品性能', icon: FaChartBar },
            { key: 'health', label: '库存健康', icon: FaWarehouse }
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`py-4 px-2 border-b-2 font-medium text-sm flex items-center ${
                activeTab === tab.key
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="mr-2" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>
    </div>
  );
  
  return (
    <div className="analytics-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800">📊 销售分析</h1>
        <p className="text-gray-600 mt-2">深度分析销售数据，洞察业务趋势</p>
      </div>
      
      <TabNavigation />
      <FilterBar />
      
      {activeTab === 'dashboard' && (
        <div>
          {dashboardLoading ? (
            <Loader />
          ) : dashboardError ? (
            <Message variant="danger">{dashboardError}</Message>
          ) : (
            <>
              <DashboardCards />
              
              {/* 趋势图表 */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-lg shadow-md p-6">
                  <h3 className="text-lg font-semibold mb-4">销售趋势</h3>
                  {trendsData && trendsData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <AreaChart data={trendsData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="period" />
                        <YAxis />
                        <Tooltip formatter={formatTooltipValue} />
                        <Area type="monotone" dataKey="total_revenue" stackId="1" stroke="#8884d8" fill="#8884d8" />
                        <Area type="monotone" dataKey="total_units" stackId="2" stroke="#82ca9d" fill="#82ca9d" />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-300 flex items-center justify-center text-gray-500">
                      暂无销售数据
                    </div>
                  )}
                </div>
                
                <div className="bg-white rounded-lg shadow-md p-6">
                  <h3 className="text-lg font-semibold mb-4">季节性销售</h3>
                  {seasonalData && seasonalData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={seasonalData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ season, total_revenue }) => `${season}: ${formatCurrency(total_revenue)}`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="total_revenue"
                        >
                          {seasonalData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-300 flex items-center justify-center text-gray-500">
                      暂无季节性数据
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}
      
      {activeTab === 'profit' && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold mb-4">利润分析</h3>
          {profitLoading ? (
            <Loader />
          ) : profitError ? (
            <Message variant="danger">{profitError}</Message>
          ) : profitData && profitData.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      产品
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      销售数量
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      总收入
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      总成本
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      总利润
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      利润率
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {profitData.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {item.chinese_name || item.product_name}
                        </div>
                        <div className="text-sm text-gray-500">{item.warehouse_name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                        {item.total_units_sold}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                        {formatCurrency(item.total_revenue)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                        {formatCurrency(item.total_cost)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                        <span className={`font-medium ${
                          parseFloat(item.total_profit) >= 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {formatCurrency(item.total_profit)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                        <span className={`font-medium ${
                          parseFloat(item.profit_margin_percentage) >= 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {parseFloat(item.profit_margin_percentage).toFixed(2)}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center text-gray-500 py-8">
              暂无利润数据
            </div>
          )}
        </div>
      )}
      
      {activeTab === 'trends' && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold mb-4">销售趋势分析</h3>
          {trendsLoading ? (
            <Loader />
          ) : trendsError ? (
            <Message variant="danger">{trendsError}</Message>
          ) : trendsData && trendsData.length > 0 ? (
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={trendsData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="period" />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" />
                <Tooltip formatter={formatTooltipValue} />
                <Legend />
                <Line yAxisId="left" type="monotone" dataKey="total_revenue" stroke="#8884d8" name="收入" />
                <Line yAxisId="right" type="monotone" dataKey="total_units" stroke="#82ca9d" name="销量" />
                <Line yAxisId="right" type="monotone" dataKey="order_count" stroke="#ffc658" name="订单数" />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center text-gray-500 py-8">
              暂无趋势数据
            </div>
          )}
        </div>
      )}
      
      {activeTab === 'seasonal' && (
        <div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold mb-4">月度销售分布</h3>
              {seasonalLoading ? (
                <Loader />
              ) : seasonalError ? (
                <Message variant="danger">{seasonalError}</Message>
              ) : monthlyData && monthlyData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={monthlyData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip formatter={formatTooltipValue} />
                    <Bar dataKey="total_revenue" fill="#8884d8" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-300 flex items-center justify-center text-gray-500">
                  暂无月度数据
                </div>
              )}
            </div>
            
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold mb-4">季节销售对比</h3>
              {seasonalLoading ? (
                <Loader />
              ) : seasonalError ? (
                <Message variant="danger">{seasonalError}</Message>
              ) : seasonalData && seasonalData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={seasonalData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ season, total_units }) => `${season}: ${total_units}瓶`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="total_units"
                    >
                      {seasonalData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-300 flex items-center justify-center text-gray-500">
                  暂无季节数据
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {activeTab === 'performance' && (
        <div className="space-y-6">
          {performanceLoading ? (
            <Loader />
          ) : performanceError ? (
            <Message variant="danger">{performanceError}</Message>
          ) : (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-lg shadow-md p-6">
                  <h3 className="text-lg font-semibold mb-4">最佳销售产品</h3>
                  {topSelling && topSelling.length > 0 ? (
                    <div className="space-y-3">
                      {topSelling.slice(0, 10).map((product, index) => (
                        <div key={product.id} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                          <div className="flex items-center">
                            <span className="text-lg font-bold text-gray-400 mr-3">#{index + 1}</span>
                            <div>
                              <div className="font-medium">{product.chinese_name || product.name}</div>
                              <div className="text-sm text-gray-500">销量: {product.total_units_sold || 0}瓶</div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="font-medium text-green-600">
                              {formatCurrency(product.total_revenue)}
                            </div>
                            <div className="text-sm text-gray-500">
                              库存: {product.current_stock || 0}瓶
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center text-gray-500 py-4">暂无销售数据</div>
                  )}
                </div>
                
                <div className="bg-white rounded-lg shadow-md p-6">
                  <h3 className="text-lg font-semibold mb-4">最高利润产品</h3>
                  {mostProfitable && mostProfitable.length > 0 ? (
                    <div className="space-y-3">
                      {mostProfitable.slice(0, 10).map((product, index) => (
                        <div key={product.id} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                          <div className="flex items-center">
                            <span className="text-lg font-bold text-gray-400 mr-3">#{index + 1}</span>
                            <div>
                              <div className="font-medium">{product.chinese_name || product.name}</div>
                              <div className="text-sm text-gray-500">
                                单瓶利润: {formatCurrency(product.avg_profit_per_unit)}
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="font-medium text-green-600">
                              {formatCurrency(product.total_profit)}
                            </div>
                            <div className="text-sm text-gray-500">
                              销量: {product.total_units_sold}瓶
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center text-gray-500 py-4">暂无利润数据</div>
                  )}
                </div>
              </div>
              
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold mb-4">库存周转率分析</h3>
                {turnoverAnalysis && turnoverAnalysis.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            产品
                          </th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            总入库
                          </th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            总出库
                          </th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            当前库存
                          </th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            周转率
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {turnoverAnalysis.map((item, index) => (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {item.chinese_name || item.name}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                              {item.total_inbound}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                              {item.total_outbound || 0}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                              {item.current_stock}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                              <span className={`font-medium ${
                                parseFloat(item.turnover_ratio) >= 1 ? 'text-green-600' : 'text-orange-600'
                              }`}>
                                {parseFloat(item.turnover_ratio).toFixed(2)}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center text-gray-500 py-4">暂无周转率数据</div>
                )}
              </div>
            </>
          )}
        </div>
      )}
      
      {activeTab === 'health' && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold mb-4">库存健康度分析</h3>
          {healthLoading ? (
            <Loader />
          ) : healthError ? (
            <ErrorDisplay error={healthError} title="库存健康度数据加载失败" />
          ) : healthData && healthData.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {healthData.map((warehouse, index) => (
                <div key={index} className="border rounded-lg p-4 hover:shadow-lg transition-shadow">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-lg font-medium">{warehouse.warehouse_name}</h4>
                    <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                      warehouse.health_score >= 80 ? 'bg-green-100 text-green-800' :
                      warehouse.health_score >= 60 ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      健康度: {warehouse.health_score}%
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">产品种类:</span>
                      <span className="font-medium">{formatNumber(warehouse.product_count)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">总库存:</span>
                      <span className="font-medium">{formatNumber(warehouse.total_units)}瓶</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">库存价值:</span>
                      <span className="font-medium">{formatCurrency(warehouse.total_value)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-red-600">缺货产品:</span>
                      <span className="font-medium text-red-600">{formatNumber(warehouse.out_of_stock_count)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-yellow-600">低库存:</span>
                      <span className="font-medium text-yellow-600">{formatNumber(warehouse.low_stock_count)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-green-600">充足库存:</span>
                      <span className="font-medium text-green-600">{formatNumber(warehouse.adequate_stock_count)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <NoDataDisplay message="暂无库存健康数据" />
          )}
        </div>
      )}
    </div>
  );
};

export default AnalyticsPage;